/**
 * UK-locale date helpers.
 *
 * WHY THIS EXISTS: The app's canonical timezone is UK (Europe/London).
 * All match dates and kickoff times in the DB are already normalised to
 * UK LOCAL. We want the UI to display them in UK local time regardless
 * of the viewer's browser timezone AND to auto-adjust across GMT ↔ BST
 * transitions.
 *
 * We achieve this by:
 *   1. Parsing a bare `YYYY-MM-DD` as NOON UTC on that calendar day —
 *      noon is safely inside the UK day boundary at any DST offset, so
 *      the calendar day never shifts.
 *   2. Rendering with `timeZone: 'Europe/London'` and locale `en-GB` so
 *      the display is stable across all viewer timezones AND the IANA
 *      `Europe/London` zone applies DST automatically.
 */

/**
 * Parse a date-like string into a `Date` that renders on the correct
 * UK calendar day regardless of the viewer's timezone.
 *
 * @param {string | null | undefined} iso  Bare `YYYY-MM-DD` or full ISO
 *   datetime. Full ISO strings are passed through unchanged.
 * @returns {Date}
 */
export function parseLocalDate(iso) {
  if (!iso) return new Date(NaN);
  const s = String(iso);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    // Noon UTC on the target calendar day. Noon is a full 11-13 hours
    // away from any DST boundary, so the day never shifts when
    // rendered with timeZone 'Europe/London'.
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
  }
  return new Date(s);
}

/**
 * Format a date-like value using UK locale + `Europe/London` timezone.
 * The IANA zone name is authoritative for DST — GMT in winter, BST in
 * summer, transitions handled automatically.
 *
 * @param {string | Date | null | undefined} iso
 * @param {Intl.DateTimeFormatOptions} [opts]
 * @returns {string}
 */
export function formatUKDate(iso, opts = { day: "numeric", month: "long" }) {
  const d = iso instanceof Date ? iso : parseLocalDate(iso);
  if (Number.isNaN(d.getTime())) return String(iso ?? "");
  return d.toLocaleDateString("en-GB", { ...opts, timeZone: "Europe/London" });
}
